import React, { useEffect, useState } from 'react';
import { apiClient } from '../lib/api';
import { SliderCategory, Slider } from '../types';
import { hasPermission } from '../utils/permissions';
import { useAuth } from '../contexts/AuthContext';
import SliderCategoryModal from './SliderCategoryModal';
import SliderModal from './SliderModal';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

const SlidersDashboard: React.FC = () => {
  const { admin } = useAuth();
  const [categories, setCategories] = useState<SliderCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<SliderCategory | null>(null);
  const [sliders, setSliders] = useState<Slider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<SliderCategory | null>(null);
  const [sliderModalOpen, setSliderModalOpen] = useState(false);
  const [editingSlider, setEditingSlider] = useState<Slider | null>(null);

  // Загрузка категорий
  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.getSliderCategories();
      setCategories(res.data);
      if (!selectedCategory && res.data.length > 0) {
        setSelectedCategory(res.data[0]);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Ошибка загрузки категорий');
    } finally {
      setLoading(false);
    }
  };

  // Загрузка слайдов выбранной категории
  const fetchSliders = async (categoryId: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.getSliders({ category_id: categoryId });
      setSliders(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Ошибка загрузки слайдов');
    } finally {
      setLoading(false);
    }
  };

  // CRUD handlers for categories
  const handleCreateCategory = async (data: Partial<SliderCategory>) => {
    await apiClient.createSliderCategory(data);
    setCategoryModalOpen(false);
    fetchCategories();
  };
  const handleEditCategory = async (data: Partial<SliderCategory>) => {
    if (!editingCategory) return;
    await apiClient.updateSliderCategory(editingCategory.id, data);
    setEditingCategory(null);
    setCategoryModalOpen(false);
    fetchCategories();
  };
  const handleDeleteCategory = async (id: string) => {
    if (window.confirm('Удалить категорию?')) {
      await apiClient.deleteSliderCategory(id);
      if (selectedCategory?.id === id) setSelectedCategory(null);
      fetchCategories();
    }
  };

  // CRUD handlers for sliders
  const handleCreateSlider = async (data: Partial<Slider>) => {
    await apiClient.createSlider(data);
    setSliderModalOpen(false);
    fetchSliders(selectedCategory!.id);
  };
  const handleEditSlider = async (data: Partial<Slider>) => {
    if (!editingSlider) return;
    await apiClient.updateSlider(editingSlider.id, data);
    setEditingSlider(null);
    setSliderModalOpen(false);
    fetchSliders(selectedCategory!.id);
  };
  const handleDeleteSlider = async (id: string) => {
    if (window.confirm('Удалить слайд?')) {
      await apiClient.deleteSlider(id);
      fetchSliders(selectedCategory!.id);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchSliders(selectedCategory.id);
    } else {
      setSliders([]);
    }
    // eslint-disable-next-line
  }, [selectedCategory]);

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-8 py-4">
      <h2 className="text-2xl font-bold mb-4">Слайдеры</h2>
      {error && <div className="text-red-600 mb-4">{error}</div>}
      <div className="flex flex-col md:flex-row gap-4 md:gap-8 min-h-[60vh]">
        {/* Категории */}
        <aside className="md:w-64 w-full md:sticky md:top-4">
          <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0">
            {categories.map(cat => (
              <div key={cat.id} className="relative flex items-center group">
                <button
                  className={`flex-1 md:flex-none whitespace-nowrap px-4 py-2 rounded-lg border transition-all duration-150 ${selectedCategory?.id === cat.id ? 'bg-blue-600 text-white border-blue-600 shadow' : 'bg-white text-gray-800 border-gray-200 hover:bg-blue-50'}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat.name}
                </button>
                {hasPermission(admin, 'slider_categories', 'update') && (
                  <button
                    className="absolute right-2 md:static ml-2 p-1 text-blue-500 hover:text-blue-700 focus:outline-none opacity-80 group-hover:opacity-100"
                    title="Редактировать категорию"
                    onClick={e => { e.stopPropagation(); setEditingCategory(cat); setCategoryModalOpen(true); }}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {hasPermission(admin, 'slider_categories', 'create') && (
              <button className="flex items-center gap-1 px-4 py-2 rounded-lg border border-dashed border-blue-400 text-blue-600 bg-blue-50 hover:bg-blue-100 transition-all duration-150" onClick={() => { setEditingCategory(null); setCategoryModalOpen(true); }}>
                <PlusIcon className="h-5 w-5" /> Категория
              </button>
            )}
          </div>
        </aside>
        {/* Слайды */}
        <main className="flex-1 flex flex-col">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
            <h3 className="text-lg font-semibold">
              {selectedCategory ? selectedCategory.name : 'Выберите категорию'}
            </h3>
            {selectedCategory && hasPermission(admin, 'sliders', 'create') && (
              <button className="hidden sm:flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition-all duration-150" onClick={() => { setEditingSlider(null); setSliderModalOpen(true); }}>
                <PlusIcon className="h-5 w-5" /> Добавить слайд
              </button>
            )}
          </div>
          {loading ? (
            <div className="flex-1 flex items-center justify-center text-gray-500">Загрузка...</div>
          ) : sliders.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-2">
              <span className="text-6xl">🖼️</span>
              <span className="text-lg">Нет слайдов в этой категории</span>
              {selectedCategory && hasPermission(admin, 'sliders', 'create') && (
                <button className="mt-4 flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition-all duration-150" onClick={() => { setEditingSlider(null); setSliderModalOpen(true); }}>
                  <PlusIcon className="h-5 w-5" /> Добавить слайд
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {sliders.map((slide, idx) => (
                <div key={slide.id} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-200 p-4 flex flex-col gap-3 group">
                  {slide.image && (
                    <img src={apiClient.getMediaUrl(slide.image)} alt={slide.alt || ''} className="w-full h-48 object-contain rounded-xl bg-gray-50" />
                  )}
                  <div className="flex-1 flex flex-col gap-1">
                    {selectedCategory?.fields_structure?.title && <div className="font-bold text-lg text-gray-900 truncate">{slide.title}</div>}
                    {selectedCategory?.fields_structure?.description && <div className="text-sm text-gray-700 line-clamp-3">{slide.description}</div>}
                    {selectedCategory?.fields_structure?.alt && <div className="text-xs text-gray-500">Alt: {slide.alt}</div>}
                    {selectedCategory?.fields_structure?.link && <a href={slide.link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 break-all hover:underline">{slide.link}</a>}
                  </div>
                  <div className="flex gap-2 mt-2">
                    {hasPermission(admin, 'sliders', 'update') && (
                      <button className="flex-1 flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-lg text-xs hover:bg-yellow-200 transition-all duration-150" onClick={() => { setEditingSlider(slide); setSliderModalOpen(true); }}>
                        <PencilIcon className="h-4 w-4" /> Редактировать
                      </button>
                    )}
                    {hasPermission(admin, 'sliders', 'delete') && (
                      <button className="flex-1 flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded-lg text-xs hover:bg-red-200 transition-all duration-150" onClick={() => handleDeleteSlider(slide.id)}>
                        <TrashIcon className="h-4 w-4" /> Удалить
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Фиксированная кнопка на мобиле */}
          {selectedCategory && hasPermission(admin, 'sliders', 'create') && (
            <button className="sm:hidden fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 px-6 py-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-150" onClick={() => { setEditingSlider(null); setSliderModalOpen(true); }}>
              <PlusIcon className="h-6 w-6" /> Добавить слайд
            </button>
          )}
        </main>
      </div>
      {/* Модалка категории */}
      <SliderCategoryModal
        open={categoryModalOpen}
        onClose={() => { setCategoryModalOpen(false); setEditingCategory(null); }}
        onSave={editingCategory ? handleEditCategory : handleCreateCategory}
        initial={editingCategory || undefined}
        categories={categories}
      />
      {/* Модалка слайда */}
      {selectedCategory && (
        <SliderModal
          open={sliderModalOpen}
          onClose={() => { setSliderModalOpen(false); setEditingSlider(null); }}
          onSave={editingSlider ? handleEditSlider : handleCreateSlider}
          initial={editingSlider || undefined}
          category={selectedCategory}
        />
      )}
    </div>
  );
};

export default SlidersDashboard; 